// Ajustes de atendimento, auditoria e relatório — Fase 14.
//
// Quatro coisas que não cabem na caixa de entrada porque não se usa enquanto se
// atende: cadastrar etiqueta, escrever resposta rápida, conferir quem mexeu no
// quê e olhar o tempo de primeira resposta.
//
// Tela de administrador do Inbox. O papel vem de `inbox_members`, e a RLS
// confere de novo do lado do banco — o `ehAdmin` daqui é conveniência de UI.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle, BarChart3, Clock, Loader2, Plus, RefreshCw, ScrollText,
  ShieldAlert, Tag, Trash2, Zap,
} from 'lucide-react';
import { useAuth } from '../../../src/contexts/AuthContext';
import { useInboxMembro } from '../../../src/hooks/useInboxMembro';
import { carregarOrgId } from './api';
import { desdeQuando } from './formato';
import type { ChannelType } from './tipos';
import type { Etiqueta, LinhaAuditoria, LinhaRelatorio, RespostaRapida } from './apiOrganizacao';
import {
  apagarEtiqueta, apagarRespostaRapida, carregarAuditoria, carregarEtiquetas, carregarRelatorio,
  carregarRespostasRapidas, criarEtiqueta, salvarRespostaRapida,
} from './apiOrganizacao';

type Aba = 'relatorio' | 'etiquetas' | 'respostas' | 'auditoria';

const CANAIS: { valor: ChannelType; rotulo: string }[] = [
  { valor: 'whatsapp_evolution', rotulo: 'WhatsApp' },
  { valor: 'whatsapp_cloud', rotulo: 'WhatsApp oficial' },
  { valor: 'instagram', rotulo: 'Instagram' },
  { valor: 'email', rotulo: 'E-mail' },
];

const NOME_CANAL: Record<string, string> = Object.fromEntries(CANAIS.map((c) => [c.valor, c.rotulo]));

/** Paleta fechada: cor digitada à mão vira etiqueta ilegível em tela escura. */
const CORES = ['#5F6C37', '#F6A937', '#2563EB', '#DC2626', '#7C3AED', '#0891B2', '#888780'];

const ACAO_LEGIVEL: Record<string, string> = {
  'conversa.atribuida': 'mudou o responsável',
  'conversa.status': 'mudou a situação',
  'conversa.etiqueta_aplicada': 'aplicou etiqueta',
  'conversa.etiqueta_removida': 'removeu etiqueta',
  'fluxo.versao_publicada': 'publicou versão de fluxo',
  'fluxo.runs_interrompidos': 'interrompeu execuções de fluxo',
};

function minutos(v: number | null): string {
  if (v === null || v === undefined) return '—';
  if (v < 60) return `${v.toFixed(0)} min`;
  const h = Math.floor(v / 60);
  const m = Math.round(v % 60);
  return m === 0 ? `${h}h` : `${h}h${String(m).padStart(2, '0')}`;
}

export default function AtendimentoView() {
  const { profile } = useAuth();
  const { ehMembro, ehAdmin, carregando: carregandoAcesso } = useInboxMembro(profile?.id);

  const [orgId, setOrgId] = useState<string | null>(null);
  const [aba, setAba] = useState<Aba>('relatorio');
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);

  const [etiquetas, setEtiquetas] = useState<Etiqueta[]>([]);
  const [respostas, setRespostas] = useState<RespostaRapida[]>([]);
  const [auditoria, setAuditoria] = useState<LinhaAuditoria[]>([]);
  const [relatorio, setRelatorio] = useState<LinhaRelatorio[]>([]);
  const [dias, setDias] = useState(30);

  const [novaTag, setNovaTag] = useState({ nome: '', cor: CORES[0] });
  const [novaResposta, setNovaResposta] = useState<{ atalho: string; body: string; canais: ChannelType[] }>({
    atalho: '', body: '', canais: [],
  });
  const [ocupado, setOcupado] = useState(false);

  const buscar = useCallback(async (org: string) => {
    setErro(null);
    try {
      const desde = new Date(Date.now() - dias * 86400_000).toISOString();
      const [t, r, rel] = await Promise.all([
        carregarEtiquetas(org),
        carregarRespostasRapidas(org),
        carregarRelatorio(org, desde, new Date().toISOString()),
      ]);
      setEtiquetas(t);
      setRespostas(r);
      setRelatorio(rel);

      // Auditoria só carrega para quem pode ler: a policy exige supervisor ou
      // admin, e para agente a consulta volta vazia. Pedir mesmo assim
      // mostraria uma aba vazia que parece bug.
      if (ehAdmin) setAuditoria(await carregarAuditoria(org));
    } catch (e) {
      setErro((e as Error).message);
    } finally {
      setCarregando(false);
    }
  }, [dias, ehAdmin]);

  useEffect(() => {
    if (!profile?.id) return;
    let vivo = true;
    carregarOrgId(profile.id)
      .then((id) => {
        if (!vivo) return;
        setOrgId(id);
        if (id) void buscar(id);
        else setCarregando(false);
      })
      .catch((e) => vivo && setErro((e as Error).message));
    return () => { vivo = false; };
  }, [profile?.id, buscar]);

  const totalPorCanal = useMemo(() => {
    const m = new Map<string, { conversas: number; respondidas: number }>();
    for (const l of relatorio) {
      const atual = m.get(l.channel_type) ?? { conversas: 0, respondidas: 0 };
      m.set(l.channel_type, {
        conversas: atual.conversas + Number(l.conversas),
        respondidas: atual.respondidas + Number(l.respondidas),
      });
    }
    return m;
  }, [relatorio]);

  if (carregandoAcesso || carregando) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="animate-spin text-gray-400" />
      </div>
    );
  }

  if (!ehMembro) {
    return (
      <div className="mx-auto mt-16 max-w-md rounded-xl bg-white p-6 text-center shadow-sm ring-1 ring-gray-200 dark:bg-slate-800 dark:ring-slate-700">
        <ShieldAlert className="mx-auto mb-3 text-gray-400" size={28} />
        <p className="font-semibold text-gray-800 dark:text-slate-100">Acesso restrito</p>
        <p className="mt-1 text-sm text-gray-500 dark:text-slate-400">
          O acesso ao Omnichannel é liberado pessoa a pessoa.
        </p>
      </div>
    );
  }

  const ABAS: { chave: Aba; rotulo: string; Icone: typeof Tag; soAdmin?: boolean }[] = [
    { chave: 'relatorio', rotulo: 'Primeira resposta', Icone: BarChart3 },
    { chave: 'etiquetas', rotulo: 'Etiquetas', Icone: Tag, soAdmin: true },
    { chave: 'respostas', rotulo: 'Respostas rápidas', Icone: Zap, soAdmin: true },
    { chave: 'auditoria', rotulo: 'Auditoria', Icone: ScrollText, soAdmin: true },
  ];

  return (
    <div className="mx-auto max-w-5xl px-4 pb-16 md:px-6">
      <header className="flex flex-wrap items-start justify-between gap-3 py-4">
        <div>
          <h1 className="text-xl font-bold text-gray-900 dark:text-white">Atendimento</h1>
          <p className="text-sm text-gray-500 dark:text-slate-400">
            Como a equipe se organiza e quanto tempo o cliente espera.
          </p>
        </div>
        <button
          onClick={() => orgId && void buscar(orgId)}
          className="flex items-center gap-1.5 rounded-xl border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-600 transition-colors hover:border-primary/40 hover:text-primary dark:border-slate-700 dark:text-slate-300"
        >
          <RefreshCw size={13} />
          Atualizar
        </button>
      </header>

      {erro && (
        <div className="mb-3 rounded-xl bg-red-50 px-3.5 py-2 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-300">
          {erro}
        </div>
      )}

      <nav className="mb-4 flex flex-wrap gap-1 border-b border-gray-200 dark:border-slate-700">
        {ABAS.filter((a) => !a.soAdmin || ehAdmin).map((a) => (
          <button
            key={a.chave}
            onClick={() => setAba(a.chave)}
            className={`flex items-center gap-1.5 border-b-2 px-3 py-2 text-xs font-semibold transition-colors ${
              aba === a.chave
                ? 'border-primary text-primary'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-slate-400 dark:hover:text-slate-200'
            }`}
          >
            <a.Icone size={13} />
            {a.rotulo}
          </button>
        ))}
      </nav>

      {/* ─── Relatório ───────────────────────────────────────────────────── */}
      {aba === 'relatorio' && (
        <section>
          <div className="mb-3 flex flex-wrap items-center gap-2">
            {[7, 30, 90].map((d) => (
              <button
                key={d}
                onClick={() => setDias(d)}
                className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                  dias === d
                    ? 'bg-primary text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-slate-700 dark:text-slate-300'
                }`}
              >
                {d} dias
              </button>
            ))}
          </div>

          {/* A mediana vem antes da média de propósito: numa amostra pequena,
              uma conversa respondida no dia seguinte joga a média para cima e
              faz o time inteiro parecer lento. */}
          <p className="mb-3 rounded-xl bg-gray-100 px-3.5 py-2 text-[11px] leading-snug text-gray-600 dark:bg-slate-700/50 dark:text-slate-300">
            O tempo conta da abertura da conversa até a primeira resposta <strong>escrita por uma
            pessoa</strong>. Resposta automática de fluxo não entra — ela mediria o robô, não o
            atendimento. A <strong>mediana</strong> mostra o dia normal; a distância dela para a
            média é o tamanho dos casos extremos.
          </p>

          {relatorio.length === 0 ? (
            <p className="rounded-2xl bg-white px-4 py-8 text-center text-xs text-gray-500 ring-1 ring-gray-200 dark:bg-slate-800 dark:text-slate-400 dark:ring-slate-700">
              Nenhuma conversa no período.
            </p>
          ) : (
            <>
              <div className="mb-3 grid grid-cols-2 gap-2 sm:grid-cols-4">
                {Array.from(totalPorCanal.entries()).map(([canal, v]) => (
                  <div key={canal} className="rounded-2xl bg-white px-3 py-2.5 ring-1 ring-gray-200 dark:bg-slate-800 dark:ring-slate-700">
                    <p className="text-[11px] font-medium uppercase tracking-wide text-gray-400 dark:text-slate-500">
                      {NOME_CANAL[canal] ?? canal}
                    </p>
                    <p className="text-lg font-bold text-gray-800 dark:text-white">{v.conversas}</p>
                    <p className="text-[11px] text-gray-500 dark:text-slate-400">
                      {v.respondidas} respondidas
                    </p>
                  </div>
                ))}
              </div>

              <div className="overflow-x-auto rounded-2xl bg-white ring-1 ring-gray-200 dark:bg-slate-800 dark:ring-slate-700">
                <table className="w-full min-w-[640px] text-left text-xs">
                  <thead className="border-b border-gray-100 text-[11px] uppercase tracking-wide text-gray-400 dark:border-slate-700 dark:text-slate-500">
                    <tr>
                      <th className="px-3 py-2 font-semibold">Canal</th>
                      <th className="px-3 py-2 font-semibold">Responsável</th>
                      <th className="px-3 py-2 font-semibold">Conversas</th>
                      <th className="px-3 py-2 font-semibold">Mediana</th>
                      <th className="px-3 py-2 font-semibold">Média</th>
                      <th className="px-3 py-2 font-semibold">Até 15 min</th>
                      <th className="px-3 py-2 font-semibold">Acima de 1h</th>
                    </tr>
                  </thead>
                  <tbody>
                    {relatorio.map((l, i) => (
                      <tr key={i} className="border-b border-gray-50 last:border-0 dark:border-slate-700/50">
                        <td className="px-3 py-2 text-gray-600 dark:text-slate-300">
                          {NOME_CANAL[l.channel_type] ?? l.channel_type}
                        </td>
                        <td className="px-3 py-2 font-medium text-gray-800 dark:text-slate-100">
                          {l.responsavel_nome}
                        </td>
                        <td className="px-3 py-2 tabular-nums text-gray-600 dark:text-slate-300">
                          {l.conversas}
                          {Number(l.respondidas) < Number(l.conversas) && (
                            <span className="ml-1 text-[10px] text-gray-400">
                              ({Number(l.conversas) - Number(l.respondidas)} sem resposta)
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-2 font-semibold tabular-nums text-gray-800 dark:text-white">
                          {minutos(l.mediana_minutos)}
                        </td>
                        <td className="px-3 py-2 tabular-nums text-gray-600 dark:text-slate-300">
                          {minutos(l.media_minutos)}
                        </td>
                        <td className="px-3 py-2 tabular-nums text-emerald-600 dark:text-emerald-400">
                          {l.ate_15_min}
                        </td>
                        <td className="px-3 py-2 tabular-nums text-red-600 dark:text-red-400">
                          {l.acima_de_1h}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </section>
      )}

      {/* ─── Etiquetas ───────────────────────────────────────────────────── */}
      {aba === 'etiquetas' && ehAdmin && (
        <section>
          <div className="mb-3 rounded-2xl bg-white p-3.5 ring-1 ring-gray-200 dark:bg-slate-800 dark:ring-slate-700">
            <div className="flex flex-wrap items-end gap-2">
              <div className="min-w-[180px] flex-1">
                <label className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-slate-400">
                  Nome da etiqueta
                </label>
                <input
                  value={novaTag.nome}
                  onChange={(e) => setNovaTag({ ...novaTag, nome: e.target.value })}
                  placeholder="Segunda via"
                  className="mt-1 w-full rounded-xl border border-gray-200 bg-white px-3 py-1.5 text-sm outline-none focus:border-primary dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100"
                />
              </div>
              <div className="flex gap-1">
                {CORES.map((c) => (
                  <button
                    key={c}
                    onClick={() => setNovaTag({ ...novaTag, cor: c })}
                    style={{ backgroundColor: c }}
                    className={`h-7 w-7 rounded-lg transition-transform ${
                      novaTag.cor === c ? 'scale-110 ring-2 ring-gray-400 ring-offset-1 dark:ring-offset-slate-800' : ''
                    }`}
                  />
                ))}
              </div>
              <button
                disabled={!novaTag.nome.trim() || ocupado}
                onClick={async () => {
                  if (!orgId) return;
                  setOcupado(true);
                  try {
                    await criarEtiqueta(orgId, novaTag.nome.trim(), novaTag.cor);
                    setNovaTag({ nome: '', cor: CORES[0] });
                    await buscar(orgId);
                  } catch (e) {
                    setErro((e as Error).message);
                  } finally {
                    setOcupado(false);
                  }
                }}
                className="flex items-center gap-1 rounded-xl bg-primary px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-40"
              >
                <Plus size={13} />
                Criar
              </button>
            </div>
          </div>

          {etiquetas.length === 0 ? (
            <p className="rounded-2xl bg-white px-4 py-8 text-center text-xs text-gray-500 ring-1 ring-gray-200 dark:bg-slate-800 dark:text-slate-400 dark:ring-slate-700">
              Nenhuma etiqueta ainda.
            </p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {etiquetas.map((t) => (
                <div
                  key={t.id}
                  className="flex items-center gap-2 rounded-full py-1 pl-3 pr-1.5 text-xs font-medium text-white"
                  style={{ backgroundColor: t.cor }}
                >
                  {t.nome}
                  <button
                    title="Apagar etiqueta"
                    onClick={async () => {
                      if (!orgId) return;
                      if (!confirm(`Apagar a etiqueta "${t.nome}"? Ela sai de todas as conversas.`)) return;
                      await apagarEtiqueta(t.id);
                      await buscar(orgId);
                    }}
                    className="rounded-full bg-black/20 p-1 transition-colors hover:bg-black/40"
                  >
                    <Trash2 size={10} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </section>
      )}

      {/* ─── Respostas rápidas ───────────────────────────────────────────── */}
      {aba === 'respostas' && ehAdmin && (
        <section>
          <div className="mb-3 rounded-2xl bg-white p-3.5 ring-1 ring-gray-200 dark:bg-slate-800 dark:ring-slate-700">
            <div className="flex flex-wrap gap-2">
              <div className="w-40">
                <label className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-slate-400">
                  Atalho
                </label>
                <input
                  value={novaResposta.atalho}
                  onChange={(e) =>
                    setNovaResposta({ ...novaResposta, atalho: e.target.value.replace(/\s+/g, '') })
                  }
                  placeholder="saudacao"
                  className="mt-1 w-full rounded-xl border border-gray-200 bg-white px-3 py-1.5 text-sm outline-none focus:border-primary dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100"
                />
              </div>
              <div className="min-w-[220px] flex-1">
                <label className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-slate-400">
                  Texto
                </label>
                <textarea
                  rows={2}
                  value={novaResposta.body}
                  onChange={(e) => setNovaResposta({ ...novaResposta, body: e.target.value })}
                  placeholder="Olá! Aqui é a Vigor Energy. Como posso ajudar?"
                  className="mt-1 w-full resize-y rounded-xl border border-gray-200 bg-white px-3 py-1.5 text-sm outline-none focus:border-primary dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100"
                />
              </div>
            </div>

            <div className="mt-2 flex flex-wrap items-end justify-between gap-2">
              <div>
                <label className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-slate-400">
                  Canais (nenhum marcado = todos)
                </label>
                <div className="mt-1 flex flex-wrap gap-1">
                  {CANAIS.map((c) => {
                    const marcado = novaResposta.canais.includes(c.valor);
                    return (
                      <button
                        key={c.valor}
                        onClick={() =>
                          setNovaResposta({
                            ...novaResposta,
                            canais: marcado
                              ? novaResposta.canais.filter((x) => x !== c.valor)
                              : [...novaResposta.canais, c.valor],
                          })
                        }
                        className={`rounded-full px-2.5 py-1 text-[11px] font-medium transition-colors ${
                          marcado
                            ? 'bg-primary text-white'
                            : 'bg-gray-100 text-gray-500 dark:bg-slate-700 dark:text-slate-400'
                        }`}
                      >
                        {c.rotulo}
                      </button>
                    );
                  })}
                </div>
              </div>
              <button
                disabled={!novaResposta.atalho.trim() || !novaResposta.body.trim() || ocupado}
                onClick={async () => {
                  if (!orgId) return;
                  setOcupado(true);
                  setErro(null);
                  try {
                    await salvarRespostaRapida(orgId, {
                      atalho: novaResposta.atalho.trim(),
                      body: novaResposta.body.trim(),
                      channel_types: novaResposta.canais.length ? novaResposta.canais : null,
                    });
                    setNovaResposta({ atalho: '', body: '', canais: [] });
                    await buscar(orgId);
                  } catch (e) {
                    setErro((e as Error).message);
                  } finally {
                    setOcupado(false);
                  }
                }}
                className="flex items-center gap-1 rounded-xl bg-primary px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-40"
              >
                <Plus size={13} />
                Criar
              </button>
            </div>
          </div>

          {respostas.length === 0 ? (
            <p className="rounded-2xl bg-white px-4 py-8 text-center text-xs text-gray-500 ring-1 ring-gray-200 dark:bg-slate-800 dark:text-slate-400 dark:ring-slate-700">
              Nenhuma resposta rápida ainda. Quem atende chama pelo atalho, digitando “/” no começo
              da mensagem.
            </p>
          ) : (
            <div className="space-y-1.5">
              {respostas.map((r) => (
                <div
                  key={r.id}
                  className="flex items-start gap-3 rounded-2xl bg-white px-3.5 py-2.5 ring-1 ring-gray-200 dark:bg-slate-800 dark:ring-slate-700"
                >
                  <span className="shrink-0 rounded bg-primary/10 px-1.5 py-0.5 font-mono text-[11px] font-semibold text-primary">
                    /{r.atalho}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="whitespace-pre-wrap text-xs text-gray-700 dark:text-slate-200">{r.body}</p>
                    <p className="mt-0.5 text-[11px] text-gray-400 dark:text-slate-500">
                      {r.channel_types?.length
                        ? r.channel_types.map((c) => NOME_CANAL[c] ?? c).join(', ')
                        : 'todos os canais'}
                    </p>
                  </div>
                  <button
                    onClick={async () => {
                      if (!orgId) return;
                      if (!confirm(`Apagar a resposta "/${r.atalho}"?`)) return;
                      await apagarRespostaRapida(r.id);
                      await buscar(orgId);
                    }}
                    className="shrink-0 rounded-lg p-1.5 text-gray-300 transition-colors hover:bg-red-50 hover:text-red-500 dark:hover:bg-red-900/20"
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </section>
      )}

      {/* ─── Auditoria ───────────────────────────────────────────────────── */}
      {aba === 'auditoria' && ehAdmin && (
        <section>
          <p className="mb-3 flex items-start gap-1.5 rounded-xl bg-gray-100 px-3.5 py-2 text-[11px] leading-snug text-gray-600 dark:bg-slate-700/50 dark:text-slate-300">
            <Clock size={12} className="mt-0.5 shrink-0" />
            Quem mudou responsável, situação e etiqueta — e quando. O registro é escrito pelo banco,
            não pela tela: não dá para agir sem deixar rastro.
          </p>

          {auditoria.length === 0 ? (
            <p className="rounded-2xl bg-white px-4 py-8 text-center text-xs text-gray-500 ring-1 ring-gray-200 dark:bg-slate-800 dark:text-slate-400 dark:ring-slate-700">
              Nada registrado ainda.
            </p>
          ) : (
            <div className="space-y-1">
              {auditoria.map((l) => (
                <div
                  key={l.id}
                  className="flex flex-wrap items-center gap-x-2 gap-y-0.5 rounded-xl bg-white px-3.5 py-2 text-xs ring-1 ring-gray-200 dark:bg-slate-800 dark:ring-slate-700"
                >
                  <span className="font-medium text-gray-700 dark:text-slate-200">
                    {ACAO_LEGIVEL[l.action] ?? l.action}
                  </span>
                  {l.diff?.de !== undefined && (
                    <span className="text-gray-400 dark:text-slate-500">
                      {String(l.diff.de ?? 'ninguém')} → {String(l.diff.para ?? 'ninguém')}
                    </span>
                  )}
                  {typeof l.diff?.tag === 'string' && (
                    <span className="text-gray-400 dark:text-slate-500">{l.diff.tag}</span>
                  )}
                  <span className="ml-auto text-[11px] text-gray-400 dark:text-slate-500">
                    {desdeQuando(l.created_at)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </section>
      )}

      {!ehAdmin && aba !== 'relatorio' && (
        <p className="flex items-center gap-1.5 rounded-xl bg-gray-100 px-3.5 py-2 text-xs text-gray-600 dark:bg-slate-700/50 dark:text-slate-300">
          <AlertTriangle size={13} />
          Só administradores do Omnichannel configuram etiquetas e respostas rápidas.
        </p>
      )}
    </div>
  );
}
